import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Typography, Card, message, Row, Col, DatePicker, Tooltip, Popconfirm, Input as AntInput, Grid } from 'antd';
import { PlusOutlined, PrinterOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;

const JournalEntriesPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { hasPermission } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchEntries();
    }, []);

    const fetchEntries = async (dates = null) => {
        setLoading(true);
        try {
            const params = {};
            if (dates) {
                params.start_date = dates[0].format('YYYY-MM-DD');
                params.end_date = dates[1].format('YYYY-MM-DD');
            }
            const { data } = await client.get('/accounting/journal-entries', { params });
            // Handle both simple array and paginated response defensively
            setEntries(Array.isArray(data) ? data : (data.data || []));
        } catch (error) {
            message.error('Failed to load journal entries');
        } finally {
            setLoading(false);
        }
    };

    const handlePost = async (id) => {
        try {
            await client.post(`/accounting/journal-entries/${id}/post`);
            message.success('Entry posted successfully');
            fetchEntries(dateRange);
        } catch (error) {
            message.error(error.response?.data?.message || 'Posting failed');
        }
    };

    const handleVoid = async (id) => {
        try {
            await client.post(`/accounting/journal-entries/${id}/void`);
            message.success('Entry voided successfully');
            fetchEntries(dateRange);
        } catch (error) {
            message.error(error.response?.data?.message || 'Voiding failed');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        // Simple CSV export
        const headers = ['Date', 'Reference', 'Description', 'Status', 'Total Debit'];
        const rows = (entries || []).map(e => [
            dayjs(e.entry_date).format('YYYY-MM-DD'),
            e.reference_number,
            e.description,
            e.status,
            e.items?.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0)
        ]);

        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(r => r.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `journal_entries_${dayjs().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'entry_date',
            key: 'date',
            render: (date) => dayjs(date).format('MMM D, YYYY'),
        },
        {
            title: 'Reference #',
            dataIndex: 'reference_number',
            key: 'reference',
            render: (text) => <Typography.Text strong>{text}</Typography.Text>
        },
        {
            title: 'Description / Memo',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Total Amount',
            key: 'amount',
            align: 'right',
            render: (_, record) => {
                const total = record.items?.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0) || 0;
                return `₱${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'default';
                if (status === 'POSTED') color = 'success';
                if (status === 'DRAFT') color = 'warning';
                if (status === 'VOIDED') color = 'error';
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    {record.status === 'DRAFT' && hasPermission('post_transaction') && (
                        <>
                            <Tooltip title="Post Entry">
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => handlePost(record.id)}
                                />
                            </Tooltip>
                            <Button type="link" onClick={() => navigate(`/accounting/journal-entries/${record.id}/edit`)}>Edit</Button>
                        </>
                    )}
                    {record.status === 'POSTED' && hasPermission('void_transaction') && (
                        <Popconfirm
                            title="Void this entry?"
                            onConfirm={() => handleVoid(record.id)}
                            okText="Void"
                            cancelText="Cancel"
                        >
                            <Tooltip title="Void Entry">
                                <Button
                                    type="default"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const filteredEntries = (entries || []).filter((e) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return (
            (e.reference_number || '').toLowerCase().includes(term) ||
            (e.description || '').toLowerCase().includes(term) ||
            (e.status || '').toLowerCase().includes(term)
        );
    });

    const draftCount = filteredEntries.filter(e => e && e.status === 'DRAFT').length;
    const postedCount = filteredEntries.filter(e => e && e.status === 'POSTED').length;

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Journal Entries</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Review and post manual accounting transactions.</Typography.Text>
                </div>
                <Space wrap>
                    <div className="no-print">
                        <DatePicker.RangePicker
                            onChange={(dates) => {
                                setDateRange(dates);
                                fetchEntries(dates);
                            }}
                            style={{ marginRight: 8, width: isMobile ? '100%' : undefined }}
                        />
                    </div>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrint}
                        className="no-print"
                    >
                        Print
                    </Button>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleExport}
                        className="no-print"
                    >
                        Export CSV
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => navigate('/accounting/journal-entries/new')}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px' }}
                        className="no-print"
                    >
                        New Entry
                    </Button>
                </Space>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Entries"
                        value={(entries || []).length}
                        icon={<i className="fa-solid fa-book"></i>}
                        color="#3b82f6"
                        subText="All recorded journals"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Draft Entries"
                        value={draftCount}
                        icon={<i className="fa-solid fa-pen-to-square"></i>}
                        color="#f59e0b"
                        subText="Awaiting posting"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Posted Entries"
                        value={postedCount}
                        icon={<i className="fa-solid fa-cloud-arrow-up"></i>}
                        color="#10b981"
                        subText="Officially recorded"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0 }}>
                    <AntInput
                        placeholder="Search reference, description, status..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: isMobile ? '100%' : 340, borderRadius: 10, height: 40 }}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={filteredEntries}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 1100 } : undefined}
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <Table
                                columns={[
                                    { title: 'Account', dataIndex: ['chart_of_account', 'name'], key: 'account' },
                                    { title: 'Debit', dataIndex: 'debit', key: 'debit', render: (val) => val > 0 ? parseFloat(val).toFixed(2) : '' },
                                    { title: 'Credit', dataIndex: 'credit', key: 'credit', render: (val) => val > 0 ? parseFloat(val).toFixed(2) : '' },
                                    { title: 'Memo', dataIndex: 'memo', key: 'memo' },
                                ]}
                                dataSource={record.items}
                                pagination={false}
                                size="small"
                                rowKey="id"
                            />
                        ),
                        rowExpandable: (record) => record.items?.length > 0,
                    }}
                />
            </Card>
        </div>
    );
};

export default JournalEntriesPage;
